import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 1500;

// Handles the redirect back from Stripe Checkout (?success=true|false).
const SubscriptionReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success') === 'true';
  const { refreshSubscriptionStatus, hasActiveSubscription, subscriptionStatus } = useAuth();
  const [attempts, setAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!success) return;

    if (hasActiveSubscription) {
      navigate('/connect-meli', { replace: true });
      return;
    }

    if (attempts >= MAX_POLL_ATTEMPTS) {
      setTimedOut(true);
      return;
    }

    const timer = setTimeout(async () => {
      await refreshSubscriptionStatus();
      setAttempts((a) => a + 1);
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [success, hasActiveSubscription, attempts, refreshSubscriptionStatus, navigate]);

  if (!success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Pago cancelado</h1>
          <p className="text-muted-foreground mb-6">No se completó el pago. Puedes intentarlo de nuevo cuando quieras.</p>
          <Button onClick={() => navigate('/select-plan')} className="bg-blue-600 hover:bg-blue-500 text-white">
            Volver a los planes
          </Button>
        </div>
      </div>
    );
  }

  if (timedOut) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Estamos confirmando tu pago</h1>
          <p className="text-muted-foreground mb-6">
            Esto está tomando más de lo usual. Si tu pago fue exitoso, tu suscripción se activará en breve — puedes
            intentar continuar.
          </p>
          <Button onClick={() => navigate('/select-plan')} className="bg-blue-600 hover:bg-blue-500 text-white">
            Volver a los planes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Confirmando tu pago...</h1>
        <p className="text-muted-foreground">
          {subscriptionStatus === 'none' || subscriptionStatus === null
            ? 'Esto solo toma unos segundos'
            : 'Casi listo'}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionReturn;
